/**
 * Ordering the auction list under a price-band side (CSE-13). Pure, so the ranking
 * rules are testable without mounting the drawer.
 */
import type { SalesAuctionBands } from '../../api/types';

export type AuctionSort = 'volume' | 'retention_asc' | 'retention_desc';

/** 'all' ranks on the auction's overall retention; a band_ord ranks on that band only. */
export type BandPick = 'all' | number;

/** Under this many units in the band, a %MMR is an anecdote: listed after the ranked lanes. */
export const MIN_RANK_UNITS = 10;

export interface RankedAuction {
  auction: SalesAuctionBands;
  units: number;
  retention: number | null;
  /** False when too thin to rank: shown after the ranked lanes, never interleaved. */
  ranked: boolean;
}

function readBand(a: SalesAuctionBands, band: BandPick): { units: number; retention: number | null } {
  if (band === 'all') return { units: a.units, retention: a.pct_mmr ?? null };
  const cell = a.bands.find((b) => b.band_ord === band);
  return { units: cell?.units ?? 0, retention: cell?.pct_mmr ?? null };
}

export function sortAuctions(
  auctions: SalesAuctionBands[],
  sort: AuctionSort,
  band: BandPick,
): RankedAuction[] {
  const rows: RankedAuction[] = auctions
    .map((auction) => {
      const { units, retention } = readBand(auction, band);
      return { auction, units, retention, ranked: retention != null && units >= MIN_RANK_UNITS };
    })
    // A band pick hides lanes that never traded in that band.
    .filter((r) => band === 'all' || r.units > 0);

  const byVolume = (a: RankedAuction, b: RankedAuction) =>
    b.units - a.units || a.auction.auction.localeCompare(b.auction.auction);

  if (sort === 'volume') return [...rows].sort(byVolume);

  const dir = sort === 'retention_asc' ? 1 : -1;
  const ranked = rows.filter((r) => r.ranked)
    .sort((a, b) => dir * ((a.retention as number) - (b.retention as number)) || byVolume(a, b));
  const unranked = rows.filter((r) => !r.ranked).sort(byVolume);
  return [...ranked, ...unranked];
}
